import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { LoyaltyTier } from '../models/LoyaltyTier'
import { loyaltyTierSchema } from '../models/schema'
import { fetchLoyaltyTiers } from '../services/LoyaltyTiersService'
import { AppDispatch, RootState } from './store'

const initialState: SBState<LoyaltyTier> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'loyaltyTier',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getLoyaltyTiersSuccess,
  setQuery: setLoyaltyTiersQuery,
  reset: resetLoyaltyTiersState,
  resetQueryAndIds: resetLoyaltyTiersQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<RootState, { [key: number]: LoyaltyTier }> = (
  state: RootState
) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectLoyaltyTiers = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedLoyaltyTier = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectLoyaltyTierById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectLoyaltyTiersByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedLoyaltyTier =
  (loyaltyTiers: LoyaltyTier | number) => async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(getIdOrModelId<LoyaltyTier>(loyaltyTiers))
    )

export const getLoyaltyTiers = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchLoyaltyTiers(params),
    [loyaltyTierSchema],
    slice.actions
  )
